// Hand-curated parent groups for the LinkedIn industry taxonomy.
//
// LinkedIn ships ~450 industries flat, which is too long for users to
// scan and pick from. We collapse them into 13 primary groups (Software
// & IT, Manufacturing, Healthcare, etc.) so users can either:
//   - click a parent pill and add every child industry as a chip
//     (the "select all in Manufacturing" pattern), or
//   - expand a parent to pick individual children, or
//   - fall back to the flat typeahead picker for fuzzy searches.
//
// The grouping is purely a frontend convenience — Crustdata's wire
// format stays `industries: [<linkedin name>, …]`. Adding a parent
// just splats every child industry name into the filter list. No
// backend change.
//
// Every industry in INDUSTRIES belongs to exactly one group. The unit
// of truth for the LinkedIn list is `src/constants/industries.ts`.
// If a new LinkedIn industry ships there, add it to one of the
// `industries` arrays below or the typeahead will keep working but
// the parent picker won't surface it.

import { INDUSTRIES } from './industries';

export interface IndustryGroup {
  /** Display name shown in the parent pill. */
  name: string;
  /** One-line description shown in a tooltip / subhead. */
  description: string;
  /** Exact LinkedIn industries this parent rolls up. */
  industries: string[];
}

export const INDUSTRY_GROUPS: IndustryGroup[] = [
  {
    name: 'Software & IT',
    description:
      'Software, IT services, security, dev tools, internet platforms',
    industries: [
      'Blockchain Services',
      'Business Intelligence Platforms',
      'Computer Networking Products',
      'Computer and Network Security',
      'Data Infrastructure and Analytics',
      'Data Security Software Products',
      'Desktop Computing Software Products',
      'Embedded Software Products',
      'IT Services and IT Consulting',
      'IT System Custom Software Development',
      'IT System Data Services',
      'IT System Design Services',
      'IT System Installation and Disposal',
      'IT System Operations and Maintenance',
      'IT System Testing and Evaluation',
      'IT System Training and Support',
      'Information Services',
      'Internet Marketplace Platforms',
      'Mobile Computing Software Products',
      'Satellite Telecommunications',
      'Security Systems Services',
      'Smart Home Technology',
      'Social Networking Platforms',
      'Software Development',
      'Technology, Information and Internet',
      'Technology, Information and Media',
      'Telecommunications',
      'Telecommunications Carriers',
      'Wireless Services',
    ],
  },
  {
    name: 'Manufacturing',
    description:
      'Industrial production, machinery, materials, consumer goods manufacturing',
    industries: [
      'Abrasives and Nonmetallic Minerals Manufacturing',
      'Agricultural Chemical Manufacturing',
      'Agriculture, Construction, Mining Machinery Manufacturing',
      'Animal Feed Manufacturing',
      'Apparel Manufacturing',
      'Appliances, Electrical, and Electronics Manufacturing',
      'Architectural and Structural Metal Manufacturing',
      'Audio and Video Equipment Manufacturing',
      'Automation Machinery Manufacturing',
      'Aviation and Aerospace Component Manufacturing',
      'Baked Goods Manufacturing',
      'Beverage Manufacturing',
      'Boilers, Tanks, and Shipping Container Manufacturing',
      'Breweries',
      'Building Materials',
      'Chemical Manufacturing',
      'Chemical Raw Materials Manufacturing',
      'Clay and Refractory Products Manufacturing',
      'Commercial and Industrial Machinery Maintenance',
      'Commercial and Service Industry Machinery Manufacturing',
      'Computer Hardware Manufacturing',
      'Computers and Electronics Manufacturing',
      'Construction Hardware Manufacturing',
      'Cutlery and Handtool Manufacturing',
      'Dairy Product Manufacturing',
      'Defense and Space Manufacturing',
      'Distilleries',
      'Electric Lighting Equipment Manufacturing',
      'Electrical Equipment Manufacturing',
      'Electronic and Precision Equipment Maintenance',
      'Engines and Power Transmission Equipment Manufacturing',
      'Fabricated Metal Products',
      'Fashion Accessories Manufacturing',
      'Food and Beverage Manufacturing',
      'Footwear Manufacturing',
      'Fruit and Vegetable Preserves Manufacturing',
      'Fuel Cell Manufacturing',
      'Furniture Manufacturing',
      'Furniture and Home Furnishings Manufacturing',
      'Glass Product Manufacturing',
      'Glass, Ceramics and Concrete Manufacturing',
      'HVAC and Refrigeration Equipment Manufacturing',
      'Household Appliance Manufacturing',
      'Industrial Automation',
      'Industrial Machinery Manufacturing',
      'Jewelry and Luxury Goods Manufacturing',
      'Leather Product Manufacturing',
      'Lime and Gypsum Products Manufacturing',
      'Machinery Manufacturing',
      'Magnetic and Optical Media Manufacturing',
      'Manufacturing',
      'Mattress and Blinds Manufacturing',
      'Measuring and Control Instrument Manufacturing',
      'Meat Products Manufacturing',
      'Metal Treatments',
      'Metal Valve, Ball, and Roller Manufacturing',
      'Metalworking Machinery Manufacturing',
      'Motor Vehicle Manufacturing',
      'Motor Vehicle Parts Manufacturing',
      'Nanotechnology Research',
      'Office Furniture and Fixtures Manufacturing',
      'Oil and Coal Product Manufacturing',
      'Organic Chemical Manufacturing',
      'Packaging and Containers Manufacturing',
      'Paint, Coating, and Adhesive Manufacturing',
      'Paper and Forest Product Manufacturing',
      'Paper and Forest Products',
      'Personal Care Product Manufacturing',
      'Plastics Manufacturing',
      'Plastics and Rubber Product Manufacturing',
      'Primary Metal Manufacturing',
      'Printed Circuit Boards Manufacturing',
      'Printing',
      'Railroad Equipment Manufacturing',
      'Rubber Products Manufacturing',
      'Seafood Product Manufacturing',
      'Semiconductor Manufacturing',
      'Shipbuilding',
      'Soap and Cleaning Product Manufacturing',
      'Space Research and Technology',
      'Sporting Goods Manufacturing',
      'Spring and Wire Product Manufacturing',
      'Steel Manufacturing',
      'Structural Metal Manufacturing',
      'Sugar and Confectionery Product Manufacturing',
      'Textile Manufacturing',
      'Tobacco Manufacturing',
      'Transportation Equipment Manufacturing',
      'Turned Products and Fastener Manufacturing',
      'Wineries',
      'Wood Product Manufacturing',
    ],
  },
  {
    name: 'Healthcare & Life Sciences',
    description: 'Hospitals, biotech, pharma, medical devices, providers',
    industries: [
      'Alternative Medicine',
      'Ambulance Services',
      'Biotechnology',
      'Biotechnology Research',
      'Dentists',
      'Family Planning Centers',
      'Healthcare',
      'Home Health Care Services',
      'Hospitals',
      'Hospitals and Health Care',
      'Medical Device Manufacturing',
      'Medical Equipment Manufacturing',
      'Medical Practices',
      'Medical and Diagnostic Laboratories',
      'Mental Health Care',
      'Nursing Homes and Residential Care Facilities',
      'Optometrists',
      'Pharmaceutical Manufacturing',
      'Pharmaceuticals',
      'Physicians',
      'Public Health',
      'Services for the Elderly and Disabled',
      'Veterinary Services',
      'Vocational Rehabilitation Services',
      'Wellness and Fitness Services',
    ],
  },
  {
    name: 'Financial Services',
    description: 'Banking, insurance, investment, fintech, capital markets',
    industries: [
      'Accounting',
      'Banking',
      'Capital Markets',
      'Claims Adjusting, Actuarial Services',
      'Collection Agencies',
      'Credit Intermediation',
      'Financial Services',
      'Funds and Trusts',
      'Holding Companies',
      'Insurance',
      'Insurance Agencies and Brokerages',
      'Insurance Carriers',
      'Insurance and Employee Benefit Funds',
      'Investment Advice',
      'Investment Banking',
      'Investment Management',
      'Loan Brokers',
      'Pension Funds',
      'Savings Institutions',
      'Securities and Commodity Exchanges',
      'Venture Capital and Private Equity Principals',
    ],
  },
  {
    name: 'Retail & Consumer',
    description: 'Stores, wholesale, consumer goods, personal services',
    industries: [
      'Apparel and Fashion',
      'Business Supplies and Equipment',
      'Consumer Electronics',
      'Consumer Goods',
      'Consumer Services',
      'Food and Beverage Retail',
      'Footwear and Leather Goods Repair',
      'Laundry and Drycleaning Services',
      'Online and Mail Order Retail',
      'Personal Care Services',
      'Personal and Laundry Services',
      'Pet Services',
      'Repair and Maintenance',
      'Retail',
      'Retail Apparel and Fashion',
      'Retail Appliances, Electrical, and Electronic Equipment',
      'Retail Art Dealers',
      'Retail Art Supplies',
      'Retail Books and Printed News',
      'Retail Building Materials and Garden Equipment',
      'Retail Florists',
      'Retail Furniture and Home Furnishings',
      'Retail Gasoline',
      'Retail Groceries',
      'Retail Health and Personal Care Products',
      'Retail Luxury Goods and Jewelry',
      'Retail Motor Vehicles',
      'Retail Musical Instruments',
      'Retail Office Equipment',
      'Retail Office Supplies and Gifts',
      'Retail Pharmacies',
      'Retail Recyclable Materials & Used Merchandise',
      'Reupholstery and Furniture Repair',
      'Vehicle Repair and Maintenance',
      'Wholesale',
      'Wholesale Alcoholic Beverages',
      'Wholesale Apparel and Sewing Supplies',
      'Wholesale Appliances, Electrical, and Electronics',
      'Wholesale Building Materials',
      'Wholesale Chemical and Allied Products',
      'Wholesale Computer Equipment',
      'Wholesale Drugs and Sundries',
      'Wholesale Food and Beverage',
      'Wholesale Footwear',
      'Wholesale Furniture and Home Furnishings',
      'Wholesale Hardware, Plumbing, Heating Equipment',
      'Wholesale Import and Export',
      'Wholesale Luxury Goods and Jewelry',
      'Wholesale Machinery',
      'Wholesale Metals and Minerals',
      'Wholesale Motor Vehicles and Parts',
      'Wholesale Paper Products',
      'Wholesale Petroleum and Petroleum Products',
      'Wholesale Photography Equipment and Supplies',
      'Wholesale Raw Farm Products',
      'Wholesale Recyclable Materials',
    ],
  },
  {
    name: 'Media & Entertainment',
    description: 'Publishing, broadcasting, music, film, sports, gaming',
    industries: [
      'Animation',
      'Animation and Post-production',
      'Artists and Writers',
      'Arts and Crafts',
      'Blogs',
      'Book Publishing',
      'Book and Periodical Publishing',
      'Broadcast Media Production and Distribution',
      'Business Content',
      'Cable and Satellite Programming',
      'Circuses and Magic Shows',
      'Computer Games',
      'Dance Companies',
      'Entertainment Providers',
      'Film and Sound Recording Industries',
      'Fine Art',
      'Internet News',
      'Internet Publishing',
      'Media Production',
      'Media and Telecommunications',
      'Mobile Gaming Apps',
      'Movies and Sound Recording',
      'Movies, Videos, and Sound',
      'Museums',
      'Music',
      'Musicians',
      'Newspaper Publishing',
      'Online Audio and Video Media',
      'Performing Arts',
      'Performing Arts and Spectator Sports',
      'Periodical Publishing',
      'Photography',
      'Publishing',
      'Radio and Television Broadcasting',
      'Sheet Music Publishing',
      'Sound Recording',
      'Spectator Sports',
      'Sports Teams and Clubs',
      'Television Broadcasting',
      'Theater Companies',
      'Writing and Editing',
    ],
  },
  {
    name: 'Real Estate & Construction',
    description: 'Construction, contractors, real estate, property services',
    industries: [
      'Building Construction',
      'Building Equipment Contractors',
      'Building Finishing Contractors',
      'Building Structure and Exterior Contractors',
      'Commercial and Industrial Equipment Rental',
      'Construction',
      'Equipment Rental Services',
      'Facilities Services',
      'Heating, Ventilation, and Air Conditioning (HVAC)',
      'Highway, Street, and Bridge Construction',
      'Household Services',
      'Janitorial Services',
      'Landscape Architecture',
      'Landscaping Services',
      'Leasing Non-residential Real Estate',
      'Leasing Residential Real Estate',
      'Nonresidential Building Construction',
      'Pest Control Services',
      'Plumbing and HVAC Services',
      'Real Estate',
      'Real Estate Agents and Brokers',
      'Real Estate and Equipment Rental Services',
      'Residential Building Construction',
      'Specialty Trade Contractors',
      'Subdivision of Land',
    ],
  },
  {
    name: 'Energy, Utilities & Resources',
    description:
      'Oil, gas, power, renewables, mining, water, waste, agriculture, forestry',
    industries: [
      'Air, Water, and Waste Program Management',
      'Coal Mining',
      'Electric Power Generation',
      'Electric Power Transmission, Control, and Distribution',
      'Environmental Services',
      'Farming',
      'Farming, Ranching, Forestry',
      'Fisheries',
      'Forestry and Logging',
      'Gas Distribution',
      'Horticulture',
      'Hydroelectric Power Generation',
      'Logging',
      'Metal Ore Mining',
      'Mining',
      'Natural Gas Distribution',
      'Natural Gas Extraction',
      'Nonmetallic Mineral Mining',
      'Nuclear Electric Power Generation',
      'Oil Extraction',
      'Oil and Gas',
      'Oil, Gas, and Mining',
      'Ranching',
      'Ranching and Fisheries',
      'Renewable Energy Equipment Manufacturing',
      'Renewable Energy Power Generation',
      'Renewable Energy Semiconductor Manufacturing',
      'Services for Renewable Energy',
      'Solar Electric Power Generation',
      'Steam and Air-Conditioning Supply',
      'Utilities',
      'Utilities Administration',
      'Utility System Construction',
      'Waste Collection',
      'Waste Treatment and Disposal',
      'Water Supply and Irrigation Systems',
      'Water, Waste, Steam, and Air Conditioning Services',
      'Wind Electric Power Generation',
    ],
  },
  {
    name: 'Transportation & Logistics',
    description:
      'Trucking, airlines, freight, maritime, warehousing, supply chain',
    industries: [
      'Airlines and Aviation',
      'Freight and Package Transportation',
      'Ground Passenger Transportation',
      'Maritime Transportation',
      'Pipeline Transportation',
      'Postal Services',
      'Rail Transportation',
      'Shuttle and Charter Bus Services',
      'Sightseeing Transportation',
      'Taxi and Limousine Services',
      'Transportation, Logistics, Supply Chain and Storage',
      'Truck Transportation',
      'Trucking',
      'Urban Transit Services',
      'Warehousing',
      'Warehousing and Storage',
    ],
  },
  {
    name: 'Education',
    description: 'Schools, higher education, training, e-learning, libraries',
    industries: [
      'Child Day Care Services',
      'Cosmetology and Barber Schools',
      'E-Learning Providers',
      'Education',
      'Education Administration Programs',
      'Flight Training',
      'Higher Education',
      'Language Schools',
      'Libraries',
      'Primary and Secondary Education',
      'Professional Training and Coaching',
      'Research',
      'Secretarial Schools',
      'Sports and Recreation Instruction',
      'Technical and Vocational Training',
    ],
  },
  {
    name: 'Government & Nonprofit',
    description: 'Government, military, NGOs, public policy, civic services',
    industries: [
      'Administration of Justice',
      'Armed Forces',
      'Civic and Social Organizations',
      'Community Development and Urban Planning',
      'Community Services',
      'Conservation Programs',
      'Correctional Institutions',
      'Courts of Law',
      'Economic Programs',
      'Emergency and Relief Services',
      'Environmental Quality Programs',
      'Fire Protection',
      'Fundraising',
      'Government Administration',
      'Government Relations Services',
      'Health and Human Services',
      'Housing Programs',
      'Housing and Community Development',
      'Individual and Family Services',
      'International Affairs',
      'International Trade and Development',
      'Judiciary',
      'Law Enforcement',
      'Legislative Offices',
      'Military and International Affairs',
      'Non-profit Organization Management',
      'Non-profit Organizations',
      'Philanthropic Fundraising Services',
      'Political Organizations',
      'Professional Organizations',
      'Public Assistance Programs',
      'Public Policy',
      'Public Policy Offices',
      'Public Safety',
      'Religious Institutions',
      'Think Tanks',
      'Transportation Programs',
    ],
  },
  {
    name: 'Professional Services',
    description:
      'Consulting, legal, design, HR, marketing, advertising, engineering',
    industries: [
      'Accessible Architecture and Design',
      'Administrative and Support Services',
      'Advertising Services',
      'Alternative Dispute Resolution',
      'Architecture and Planning',
      'Business Consulting and Services',
      'Civil Engineering',
      'Design Services',
      'Engineering Services',
      'Executive Offices',
      'Executive Search Services',
      'Funeral Services',
      'Graphic Design',
      'Human Resources Services',
      'Interior Design',
      'Law Practice',
      'Legal Services',
      'Management Consulting',
      'Market Research',
      'Marketing Services',
      'Office Administration',
      'Operations Consulting',
      'Outsourcing and Offshoring Consulting',
      'Printing Services',
      'Professional Services',
      'Public Relations and Communications Services',
      'Research Services',
      'Security Guards and Patrol Services',
      'Security and Investigations',
      'Staffing and Recruiting',
      'Strategic Management Services',
      'Support Services',
      'Surveying and Mapping Services',
      'Telephone Call Centers',
      'Temporary Help Services',
      'Translation and Localization',
    ],
  },
  {
    name: 'Hospitality, Travel & Recreation',
    description:
      'Hotels, restaurants, tourism, events, sports facilities, attractions',
    industries: [
      'Accommodation Services',
      'Amusement Parks and Arcades',
      'Bars, Taverns, and Nightclubs',
      'Bed-and-Breakfasts, Hostels, Homestays',
      'Caterers',
      'Events Services',
      'Food and Beverage Services',
      'Gambling Facilities and Casinos',
      'Golf Courses and Country Clubs',
      'Historical Sites',
      'Hospitality',
      'Hotels and Motels',
      'Leisure, Travel & Tourism',
      'Mobile Food Services',
      'Museums, Historical Sites, and Zoos',
      'Recreational Facilities',
      'Recreational Facilities and Services',
      'Restaurants',
      'Skiing Facilities',
      'Travel Arrangements',
      'Zoos and Botanical Gardens',
    ],
  },
];

// Reverse lookup: industry → group name. Built once at module load.
const _industryToGroupName: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const group of INDUSTRY_GROUPS) {
    for (const industry of group.industries) {
      map[industry] = group.name;
    }
  }
  return map;
})();

// ── Picker / BE expansion ─────────────────────────────────────────────
//
// FilterPicker.grouped-list mode stores group-name tokens (e.g.
// "Software & IT") in the same array as individual LinkedIn industry
// names. The BE only knows the canonical industry strings, so wrap
// every send-to-Crustdata call site with ``expandIndustrySelection``
// to swap group tokens for their member lists before firing.

const _industryGroupByName: Record<string, IndustryGroup> = (() => {
  const map: Record<string, IndustryGroup> = {};
  for (const g of INDUSTRY_GROUPS) map[g.name] = g;
  return map;
})();

/** Expand any group-name tokens in ``values`` to their underlying
 *  LinkedIn industries. Individual industry names pass through
 *  unchanged. Result is de-duplicated. */
export function expandIndustrySelection(values: string[]): string[] {
  if (values.length === 0) return values;
  const out = new Set<string>();
  for (const v of values) {
    const group = _industryGroupByName[v];
    if (group) {
      for (const industry of group.industries) out.add(industry);
    } else {
      out.add(v);
    }
  }
  return Array.from(out);
}

/** All group-token names — for FilterPicker's ``groupTokens`` prop. */
export const INDUSTRY_GROUP_NAMES: string[] = INDUSTRY_GROUPS.map(
  (g) => g.name
);

// Dev-time sanity check: every industry in INDUSTRIES must land in
// exactly one group. Logged once (not thrown) so a stale taxonomy
// doesn't break the sidebar — the typeahead picker still works as
// fallback.
if (import.meta.env.DEV) {
  const grouped = new Set(Object.keys(_industryToGroupName));
  const missing = INDUSTRIES.filter((i) => !grouped.has(i));
  if (missing.length > 0) {
    console.warn(
      `[industry-groups] ${missing.length} industries are not in any group:`,
      missing.slice(0, 10)
    );
  }
}
